import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

import { Button } from '@/components/ui/button';

// Phase 8.5-05 — page-jumper primitive (ADR-014). 1-indexed pagination —
// `page` and `totalPages` are both 1-based to match backend contract.
// `totalPages === 0` (no rows) renders the bar disabled with «Page 0 of 0»,
// not hidden — keeps grid layout stable when filters return zero results.
export interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  className?: string;
}

export const Pagination = ({
  page,
  totalPages,
  onPageChange,
  disabled = false,
  className,
}: PaginationProps) => {
  const safeTotal = Math.max(0, totalPages);
  const safePage = safeTotal === 0 ? 0 : Math.min(Math.max(1, page), safeTotal);
  const atFirst = disabled || safePage <= 1;
  const atLast = disabled || safePage >= safeTotal;

  return (
    <div
      className={`flex items-center justify-end gap-2 ${className ?? ''}`}
      role="navigation"
      aria-label="Pagination"
    >
      <span className="mr-2 text-sm text-muted-foreground">
        Page {safePage} of {safeTotal}
      </span>
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label="First page"
        disabled={atFirst}
        onClick={() => {
          onPageChange(1);
        }}
      >
        <ChevronsLeft className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label="Previous page"
        disabled={atFirst}
        onClick={() => {
          onPageChange(safePage - 1);
        }}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label="Next page"
        disabled={atLast}
        onClick={() => {
          onPageChange(safePage + 1);
        }}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label="Last page"
        disabled={atLast}
        onClick={() => {
          onPageChange(safeTotal);
        }}
      >
        <ChevronsRight className="h-4 w-4" />
      </Button>
    </div>
  );
};
